/**
 * Vendor Payment Methods Card (Iter 63).
 *
 * Vendors choose which methods customers can use to pay them the RENTAL BALANCE
 * (not the deposit — that's always cash) at handover:
 *   • Cash (always available, can't be fully turned off)
 *   • PayPal — vendor saves PayPal.me link AND/OR PayPal account email
 *   • Stripe — vendor saves their own Stripe Payment Link
 *
 * Customers pick one of the enabled methods at checkout; the booking confirmation
 * email + rental agreement PDF surface the chosen method's contact info.
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Wallet, Banknote, ExternalLink, Info } from "lucide-react";
import { toast } from "sonner";

export default function VendorPaymentMethodsCard() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/auth/me");
      setData({
        accepts_cash: data.accepts_cash !== false,           // default true
        accepts_paypal: data.accepts_paypal !== false,
        accepts_stripe: data.accepts_stripe !== false,
        paypal_email: data.paypal_email || "",
        paypal_me_link: data.paypal_me_link || "",
        stripe_payment_link: data.stripe_payment_link || "",
      });
    } catch (e) {
      console.error("payment methods load failed:", e);
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return null;

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/users/me/business", {
        accepts_cash: data.accepts_cash,
        accepts_paypal: data.accepts_paypal,
        accepts_stripe: data.accepts_stripe,
        paypal_email: data.paypal_email.trim(),
        paypal_me_link: data.paypal_me_link.trim(),
        stripe_payment_link: data.stripe_payment_link.trim(),
      });
      toast.success("Payment methods updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const paypalReady = data.accepts_paypal && (data.paypal_email.trim() || data.paypal_me_link.trim());
  const stripeReady = data.accepts_stripe && data.stripe_payment_link.trim();

  return (
    <div className="rounded-2xl border border-border bg-white p-4 sm:p-5 shadow-sm" data-testid="vendor-payment-methods-card">
      <div className="flex items-center gap-2 mb-3">
        <Wallet className="h-4 w-4 text-blue-600" />
        <h2 className="font-display text-lg">Payment methods you accept</h2>
      </div>
      <p className="text-xs text-neutral-500 mb-4">
        Customers pick one of these to pay you the rental balance at pickup.
        The refundable deposit is always cash.
      </p>

      <div className="space-y-3">
        {/* Cash */}
        <div className="flex items-center justify-between rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5" data-testid="vendor-pm-cash">
          <div className="flex items-center gap-2">
            <Banknote className="h-4 w-4 text-emerald-600" />
            <div>
              <div className="font-semibold text-sm">Cash at pickup</div>
              <div className="text-[11px] text-neutral-500">Customer pays the balance in person</div>
            </div>
          </div>
          <Switch
            checked={data.accepts_cash}
            onCheckedChange={(v) => setData({ ...data, accepts_cash: v })}
            data-testid="vendor-pm-cash-toggle"
          />
        </div>

        {/* PayPal */}
        <div className="rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5 space-y-2" data-testid="vendor-pm-paypal">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded-full bg-[#0070ba] flex items-center justify-center text-white text-[8px] font-bold">P</div>
              <div>
                <div className="font-semibold text-sm">PayPal</div>
                <div className="text-[11px] text-neutral-500">
                  {paypalReady ? "Ready" : "Add a link or email to activate"}
                </div>
              </div>
            </div>
            <Switch
              checked={data.accepts_paypal}
              onCheckedChange={(v) => setData({ ...data, accepts_paypal: v })}
              data-testid="vendor-pm-paypal-toggle"
            />
          </div>
          {data.accepts_paypal && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <Input
                placeholder="https://paypal.me/yourhandle"
                value={data.paypal_me_link}
                onChange={(e) => setData({ ...data, paypal_me_link: e.target.value })}
                className="text-sm"
                data-testid="vendor-pm-paypal-link"
              />
              <Input
                placeholder="business@email.com"
                value={data.paypal_email}
                onChange={(e) => setData({ ...data, paypal_email: e.target.value })}
                className="text-sm"
                data-testid="vendor-pm-paypal-email"
              />
            </div>
          )}
        </div>

        {/* Stripe */}
        <div className="rounded-xl border border-border bg-neutral-50/40 px-3 py-2.5 space-y-2" data-testid="vendor-pm-stripe">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-[#635bff] flex items-center justify-center text-white text-[8px] font-bold">S</div>
              <div>
                <div className="font-semibold text-sm">Stripe (card)</div>
                <div className="text-[11px] text-neutral-500">
                  {stripeReady ? "Ready" : "Paste your Stripe Payment Link to activate"}
                </div>
              </div>
            </div>
            <Switch
              checked={data.accepts_stripe}
              onCheckedChange={(v) => setData({ ...data, accepts_stripe: v })}
              data-testid="vendor-pm-stripe-toggle"
            />
          </div>
          {data.accepts_stripe && (
            <Input
              placeholder="https://buy.stripe.com/..."
              value={data.stripe_payment_link}
              onChange={(e) => setData({ ...data, stripe_payment_link: e.target.value })}
              className="text-sm"
              data-testid="vendor-pm-stripe-link"
            />
          )}
          {data.accepts_stripe && !stripeReady && (
            <a
              href="https://dashboard.stripe.com/payment-links"
              target="_blank"
              rel="noreferrer"
              className="text-[11px] text-blue-600 hover:underline inline-flex items-center gap-1"
              data-testid="vendor-pm-stripe-help"
            >
              How to create a Stripe Payment Link <ExternalLink className="h-3 w-3" />
            </a>
          )}
        </div>
      </div>

      <div className="flex items-start gap-2 mt-4 text-[11px] text-neutral-500">
        <Info className="h-3 w-3 mt-0.5 shrink-0" />
        <span>The refundable deposit is always collected in cash at pickup, regardless of the method chosen above.</span>
      </div>

      <Button
        onClick={save}
        disabled={busy}
        className="rounded-full bg-blue-600 hover:bg-blue-700 mt-4"
        data-testid="vendor-pm-save"
      >
        Save payment methods
      </Button>
    </div>
  );
}
